import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import Sidebar from './Sidebar';
import './AdminSettings.css';
import SetupChecklist from './SetupChecklist';

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({ dip_expiry_days: 30, nag_email_days: 3 });

  const flash = (setter, msg) => { setter(msg); setTimeout(() => setter(''), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/company-settings');
      setSettings(data);
      setForm({ dip_expiry_days: data.dip_expiry_days, nag_email_days: data.nag_email_days });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load settings');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      await api.put('/admin/company-settings', {
        dip_expiry_days: parseInt(form.dip_expiry_days),
        nag_email_days: parseInt(form.nag_email_days),
      });
      flash(setSuccess, 'Settings saved successfully');
      load();
    } catch (err) { flash(setError, err.response?.data?.error || 'Failed to save settings'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="as-loading">Loading settings...</div>;

  return (
    <div className="bc-page-layout">
      <Sidebar />
      <main className="main-content">
      <div className="as-wrap">
      <div className="as-header">
        <div className="as-header-icon">settings</div>
        <div>
          <h1 className="as-title">Company Settings</h1>
          <p className="as-subtitle">Configure your brokerage defaults and behaviour</p>
        </div>
      </div>
      <SetupChecklist token={localStorage.getItem('token')} />

      {error && <div className="as-alert as-alert--error">{error}</div>}
      {success && <div className="as-alert as-alert--success">{success}</div>}
      <form onSubmit={handleSave} className="as-form">
        <section className="as-section">
          <h2 className="as-section-title">DIP Settings</h2>
          <p className="as-section-desc">How many days after upload before a DIP expires and triggers a chase.</p>
          <div className="as-field">
            <label className="as-label" htmlFor="dip_expiry_days">
              DIP expiry
              <span className="as-label-hint">Days before a DIP is considered expired</span>
            </label>
            <div className="as-input-row">
              <input id="dip_expiry_days" name="dip_expiry_days" type="number" min="1" max="365" className="as-input as-input--short" value={form.dip_expiry_days} onChange={handleChange} />
              <span className="as-unit">days</span>
            </div>
          </div>
        </section>
        <section className="as-section">
          <h2 className="as-section-title">Email Chasing</h2>
          <p className="as-section-desc">Send a chase email if a deal has not progressed for this many days.</p>
          <div className="as-field">
            <label className="as-label" htmlFor="nag_email_days">
              Chase frequency
              <span className="as-label-hint">Days of inactivity before a chase email is sent</span>
            </label>
            <div className="as-input-row">
              <input id="nag_email_days" name="nag_email_days" type="number" min="1" max="30" className="as-input as-input--short" value={form.nag_email_days} onChange={handleChange} />
              <span className="as-unit">days</span>
            </div>
          </div>
        </section>

        <div className="as-actions">
          <button type="submit" className="as-btn as-btn--primary" disabled={saving}>{saving ? 'Saving...' : 'Save Settings'}</button>
          {settings && <span className="as-last-saved">Last updated {new Date(settings.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
        </div>
      </form>
    </div>
      </main>
    </div>
  );
}
